import { useState } from 'react';
import {
  Modal,
  TextInput,
  Textarea,
  Button,
  Select,
  Group,
  Stack,
  Text,
  Alert,
  Box,
  UnstyledButton
} from '@mantine/core';
import { IconAlertCircle, IconMessageCircle } from '@tabler/icons-react';
import { submitContextualFeedback } from '../../services/feedbackService';
import { useAuth } from '../../hooks/useAuth';

type ContextualFeedbackType = 'like' | 'dislike' | 'suggestion' | 'issue' | 'feature_request' | 'general';

type FeedbackContext = {
  type: 'meal_plan' | 'assignment' | 'quick_bite';
  mealPlanId?: string;
  assignmentId?: string;
  recipeName?: string;
  mealPlanDateRange?: string;
  recipeId?: string;
};

type ContextualFeedbackModalProps = {
  opened: boolean;
  onClose: () => void;
  context: FeedbackContext;
  title?: string;
};

export const ContextualFeedbackModal = ({ 
  opened, 
  onClose, 
  context,
  title 
}: ContextualFeedbackModalProps) => {
  const { user } = useAuth();
  const [feedbackType, setFeedbackType] = useState<ContextualFeedbackType>('general');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const feedbackTypeOptions = [
    { value: 'general', label: 'General feedback' },
    { value: 'like', label: 'I liked this' },
    { value: 'dislike', label: 'I didn\'t like this' },
    { value: 'suggestion', label: 'Suggestion for improvement' },
    { value: 'issue', label: 'Report an issue' },
    { value: 'feature_request', label: 'Feature request' },
  ];

  const getDefaultTitle = () => {
    if (title) return title;
    return 'Send feedback';
  };

  const getContextDescription = () => {
    if (context.type === 'quick_bite' && context.recipeName) {
      return `Quick Bite: ${context.recipeName}`;
    }
    if (context.type === 'assignment' && context.recipeName) {
      return context.recipeName;
    }
    return context.mealPlanDateRange ? `Meal Plan: ${context.mealPlanDateRange}` : 'This meal plan';
  };

  const resetForm = () => {
    setFeedbackType('general');
    setSubject('');
    setMessage('');
    setSubmitStatus(null);
    setErrorMessage('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!subject.trim() || !message.trim()) {
      setSubmitStatus('error');
      setErrorMessage('Please fill in both subject and message fields.');
      return;
    }

    if (!user) {
      setSubmitStatus('error');
      setErrorMessage('You must be logged in to send feedback.');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      await user.getIdToken().then(token =>
        submitContextualFeedback({
          type: feedbackType,
          subject: subject.trim(),
          message: message.trim(),
          context,
        }, token)
      );

      setSubmitStatus('success');
      handleClose();
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to send feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="sm" align="center">
          <IconMessageCircle size={20} />
          <Text fw={600} size="lg">{getDefaultTitle()}</Text>
        </Group>
      }
      centered
      size="md"
      radius="lg"
    >
      <Stack gap="md">
        <Box>
          <Text size="sm" c="dimmed" mb="md">
            {getContextDescription()}
          </Text>
        </Box>

        {submitStatus === 'error' && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error" radius="lg">
            {errorMessage}
          </Alert>
        )}

        <Select
          label="Feedback type"
          placeholder="Select feedback type"
          data={feedbackTypeOptions}
          value={feedbackType}
          onChange={(value) => setFeedbackType(value as ContextualFeedbackType)}
          required
        />

        <TextInput
          label="Subject"
          placeholder="Brief description of your feedback"
          value={subject}
          onChange={(event) => setSubject(event.currentTarget.value)}
          required
          maxLength={200}
        />

        <Textarea
          label="Details"
          placeholder="Please provide specific feedback about this meal plan or recipe..."
          value={message}
          onChange={(event) => setMessage(event.currentTarget.value)}
          required
          minRows={4}
          maxRows={8}
          autosize
          maxLength={2000}
        />

        <Text size="xs" c="dimmed">
          We appreciate you taking the time to contact us. Your input helps us improve Meals You Love!
        </Text>

        <Group justify="flex-end" mt="md" gap="lg">
          <UnstyledButton onClick={handleClose} disabled={isSubmitting}>
            <Text size="sm" c="dimmed">Cancel</Text>
          </UnstyledButton>
          <Button
            onClick={handleSubmit}
            loading={isSubmitting}
          >
            Send feedback
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
